<script setup>
import { railsApi, railsApiDownload } from '@/utils/railsApi'
import { formatCurrency } from '@core/utils/formatters'
import StatusChip from '@/components/shared/StatusChip.vue'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const success = ref('')
const invoice = ref(null)
const voidDialog = ref(false)
const voidReason = ref('')
const voiding = ref(false)
const downloading = ref(false)

const formatDate = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleDateString('nl-NL')
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const data = await railsApi(`/api/v1/admin/platform_invoices/${route.params.id}`)

    invoice.value = data.platform_invoice
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.invoices.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const downloadPdf = async () => {
  downloading.value = true
  error.value = ''
  try {
    await railsApiDownload(
      `/api/v1/admin/platform_invoices/${route.params.id}/document`,
      { filename: `${invoice.value?.invoice_number || 'factuur'}.pdf` },
    )
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.invoices.loadError')
  }
  finally {
    downloading.value = false
  }
}

const openVoid = () => {
  voidReason.value = ''
  voidDialog.value = true
}

const confirmVoid = async () => {
  if (!voidReason.value.trim())
    return

  voiding.value = true
  error.value = ''
  success.value = ''
  try {
    const data = await railsApi(`/api/v1/admin/platform_invoices/${route.params.id}/void`, {
      method: 'POST',
      body: { reason: voidReason.value.trim() },
    })

    invoice.value = data.platform_invoice
    voidDialog.value = false
    success.value = t('admin.invoices.voidSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.invoices.voidError')
  }
  finally {
    voiding.value = false
  }
}
</script>

<template>
  <div>
    <VBtn
      variant="text"
      class="mb-2 px-0"
      @click="router.push({ name: 'admin-invoices' })"
    >
      {{ $t('admin.invoices.back') }}
    </VBtn>

    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-4">
      <h1 class="text-h4 mb-0">
        {{ $t('admin.invoices.detailTitle') }}
      </h1>
      <div
        v-if="invoice"
        class="d-flex flex-wrap gap-2"
      >
        <VBtn
          color="primary"
          variant="tonal"
          :loading="downloading"
          @click="downloadPdf"
        >
          {{ $t('admin.invoices.downloadPdf') }}
        </VBtn>
        <VBtn
          v-if="invoice.voidable"
          color="warning"
          variant="tonal"
          @click="openVoid"
        >
          {{ $t('admin.invoices.void') }}
        </VBtn>
      </div>
    </div>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>
    <VAlert
      v-if="success"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ success }}
    </VAlert>
    <VAlert
      v-if="invoice?.pending_payment"
      type="warning"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ $t('admin.invoices.voidBlockedPending') }}
    </VAlert>

    <div
      v-if="loading"
      role="status"
    >
      {{ $t('common.loading') }}
    </div>

    <template v-else-if="invoice">
      <VCard
        class="mb-4"
        flat
        border
      >
        <VCardText>
          <div class="d-flex flex-wrap align-center gap-2 mb-3">
            <StatusChip
              domain="platform_invoice"
              :state="invoice.lifecycle_state"
            />
            <span class="text-medium-emphasis">{{ invoice.invoice_number }}</span>
          </div>
          <p class="mb-1">
            <strong>{{ $t('admin.invoices.trainer') }}:</strong>
            {{ invoice.trainer?.business_name || invoice.trainer?.name || invoice.trainer?.email }}
            <RouterLink
              v-if="invoice.trainer_id"
              class="ms-2"
              :to="{ name: 'admin-trainers-id', params: { id: invoice.trainer_id } }"
            >
              {{ $t('admin.invoices.openTrainer') }}
            </RouterLink>
          </p>
          <p class="mb-1">
            <strong>{{ $t('admin.invoices.description') }}:</strong>
            {{ invoice.description }}
          </p>
          <p class="mb-1">
            <strong>{{ $t('admin.invoices.period') }}:</strong>
            {{ formatDate(invoice.period_start_on) }} – {{ formatDate(invoice.period_end_on) }}
          </p>
          <p class="mb-1">
            <strong>{{ $t('admin.invoices.amount') }}:</strong>
            {{ formatCurrency(invoice.amount_cents, invoice.currency) }}
          </p>
          <p class="mb-1">
            <strong>{{ $t('admin.invoices.issued') }}:</strong>
            {{ formatDate(invoice.issued_on) }}
          </p>
          <p class="mb-1">
            <strong>{{ $t('admin.invoices.due') }}:</strong>
            {{ formatDate(invoice.due_on) }}
          </p>
          <p
            v-if="invoice.void_reason"
            class="mb-0"
          >
            <strong>{{ $t('admin.columns.reason') }}:</strong>
            {{ invoice.void_reason }}
          </p>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
      >
        <VCardItem>
          <VCardTitle>{{ $t('admin.invoices.paymentsTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="!invoice.payments?.length"
            type="info"
            variant="tonal"
            role="status"
          >
            {{ $t('admin.invoices.noPayments') }}
          </VAlert>
          <VTable
            v-else
            density="comfortable"
          >
            <thead>
              <tr>
                <th scope="col">
                  {{ $t('admin.invoices.status') }}
                </th>
                <th scope="col">
                  {{ $t('admin.invoices.amount') }}
                </th>
                <th scope="col">
                  {{ $t('admin.invoices.gatewayRef') }}
                </th>
                <th scope="col">
                  {{ $t('admin.invoices.failureReason') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="payment in invoice.payments"
                :key="payment.id"
              >
                <td>
                  <StatusChip
                    domain="platform_payment"
                    :state="payment.lifecycle_state"
                  />
                </td>
                <td>{{ formatCurrency(payment.amount_cents, payment.currency) }}</td>
                <td>{{ payment.gateway_payment_id || '—' }}</td>
                <td>{{ payment.failure_reason || '—' }}</td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </template>

    <VDialog
      v-model="voidDialog"
      max-width="520"
    >
      <VCard>
        <VCardTitle>{{ $t('admin.invoices.voidTitle') }}</VCardTitle>
        <VCardText>
          <p class="mb-3">
            {{ $t('admin.trainerDetail.dialogReasonHelp') }}
          </p>
          <VTextarea
            v-model="voidReason"
            :label="$t('admin.trainerDetail.reasonLabel')"
            rows="3"
            autofocus
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="voidDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="warning"
            :disabled="!voidReason.trim()"
            :loading="voiding"
            @click="confirmVoid"
          >
            {{ $t('admin.invoices.voidConfirm') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
